import { mapState } from 'vuex';

export default {
    computed: {
        ...mapState({
            productItems: (state) => state.products.items,
            productId: (state) => state.pages?.page.product_id,
            currency: (state) => state.locales?.locale?.currency || 'USD',
            currencyFormat: (state) => state.locales?.locale?.currency_format || '12,345,678.91',
            currencyList: (state) => state.slices?.items?.currency?.list || [],
            countryCode: (state) => state.geolocation.location?.data?.country?.code || '',
            licensesItems: (state) => state.licenses?.data || {},
        }),

        currencyItem() {
            if (!this.hasDifferentCurrencies) return {};
            return this.currencyList.find((item) => item.countryCodes?.includes(this.countryCode)) || {};
        },

        productCurrency() {
            if (this.currencyItem.currency) return this.currencyItem.currency;
            return this.productItems?.[this.productID]?.parameters?.common?.currency || this.currency;
        },

        hasDifferentCurrencies() {
            return ['en-en', 'pt-br'].includes(this.$store.state.geolocation.desiredLocale);
        },
    },

    data() {
        return {
            isLoadedPrices: true,
        };
    },

    methods: {
        setCurrency(value, settings = {}) {
            const matches = this.productCurrencyFormat(settings).match(
                /^([^\d\s]*)(\s*)(\d+)([^\d])?(\d{3})([^\d])?(\d{3})(([^\d])(\d+))?(\s*)([^\d\s]*)$/,
            );

            const price = !value && settings?.zeroByDefault ? 0 : parseFloat(value);
            if (!settings?.zeroByDefault && (!matches || price <= 0)) {
                return '???';
            }

            const currency = {
                symbol: matches[1] ? matches[1] : matches[12],
                space: matches[1] ? matches[2] : matches[11],
                position: matches[1] ? 'before' : 'after',
                decimalsDrop: !matches[8],
                decimalsCustom: matches[10] ? matches[10].length : 0,
                decimalDelimiter: matches[9] ? matches[9] : '.',
                groupDelimiter: matches[4] && matches[6] && matches[4] === matches[6] ? matches[4] : '',
                postfix: matches[1] ? matches[11] + matches[12] : '',
            };

            const source = String(
                currency.decimalsDrop ? Math.ceil(price) : price.toFixed(currency.decimalsCustom),
            );
            const integer = source.split('.')[0];
            const groupsCount = integer.length > 3 ? integer.length % 3 : 0;
            let result = groupsCount ? integer.substr(0, groupsCount) + currency.groupDelimiter : '';
            result += integer.substr(groupsCount).replace(/(\d{3})(?=\d)/g, `$1${currency.groupDelimiter}`);

            const fraction = source.split('.')[1] || 0;
            result += !currency.decimalsDrop ? currency.decimalDelimiter + fraction : '';

            if (settings && settings.withoutCurrencySymbol) {
                currency.symbol = '';
            }

            currency.space = currency.space ? ' ' : '';
            if (currency.position === 'before') {
                result = currency.symbol + currency.space + result + currency.postfix;
            } else {
                result += currency.space + currency.symbol;
            }

            return result
                .replace(/(&euro;|&pound;|&yen;)/g, '<sup>$1</sup>')
                .replaceAll(' ', '&nbsp;')
                .replace(/\$&/g, '&dollar;&'); // $& inserts the matched substring
        },

        productCurrencyFormat(settings) {
            if (this.currencyItem.currencyFormat) {
                return this.currencyItem.currencyFormat;
            }
            if (settings.format) {
                return settings.format;
            }
            const productId = settings.productId || this.productId;
            const productItem = this.productItems?.[productId] || {};
            return productItem.parameters?.common?.currency_format || this.currencyFormat;
        },

        async getCurrency() {
            if (!this.hasDifferentCurrencies) return;

            this.isLoadedPrices = false;

            await this.$store.dispatch('geolocation/getGeoLocation');

            if (!this.currencyItem.locale || !this.currencyItem.currencyFormat) {
                this.isLoadedPrices = true;
                return;
            }

            await this.getLicenses({
                locale: this.currencyItem.locale,
                isPurchase: Boolean(this.isPurchase),
                cleverBridgeCartList: this.licensesItems.map((item) => item.cleverbridge_id),
            });

            this.isLoadedPrices = true;
        },
    },

    async serverPrefetch() {
        // The currency of the country selected by the user should be used.
        const locale = this.$store.state.geolocation.desiredLocale || this.$store.state.geolocation.requestedLocale;
        await this.$store.dispatch('locales/getLocaleData', { locale });
        await this.$store.dispatch('slices/getSyncedData', { slice: 'currency', locale });
    },
};
